import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { validateResetToken, resetPassword } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Loader2, ArrowLeft, ShieldAlert, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-gym-guide/artifacts/gwvje8fa_IMG_20220211_145748_413%20%285%29.jpg";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6 sm:p-8 pb-24 sm:pb-8">
    <div className="w-full max-w-md" data-testid="reset-password-page">
      <div className="mb-12 flex justify-center">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Process Body" className="w-14 h-14 rounded-full object-cover" />
          <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            PROCESS<span className="text-[#32CD32]">BODY</span>
          </span>
        </Link>
      </div>
      {children}
    </div>
  </div>
);

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const checkToken = useCallback(async () => {
    if (!token) {
      setChecking(false);
      return;
    }
    try {
      const data = await validateResetToken(token);
      setValid(data.valid);
      setEmail(data.email || '');
    } catch {
      setValid(false);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    checkToken();
  }, [checkToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('A palavra-passe tem de ter pelo menos 8 caracteres');
      return;
    }
    if (password !== confirm) {
      toast.error('As palavras-passe não coincidem');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success('Palavra-passe alterada com sucesso!');
      setTimeout(() => navigate('/login'), 2500);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Não foi possível alterar a palavra-passe.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Shell>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#32CD32] animate-spin" />
        </div>
      </Shell>
    );
  }

  if (!valid) {
    return (
      <Shell>
        <div className="text-center" data-testid="reset-password-invalid">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
            Link inválido
          </h1>
          <p className="text-gray-400 mb-8">
            Este link de recuperação já foi usado ou expirou. Pede um novo para definires a tua palavra-passe.
          </p>
          <Link to="/forgot-password">
            <Button className="btn-primary h-12 rounded-xl px-8" data-testid="reset-password-request-new-btn">
              PEDIR NOVO LINK
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center" data-testid="reset-password-success">
          <div className="w-16 h-16 rounded-full bg-[#32CD32]/15 border border-[#32CD32]/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#32CD32]" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
            Tudo pronto
          </h1>
          <p className="text-gray-400 mb-8">
            A tua palavra-passe foi alterada. Vamos levar-te ao login...
          </p>
          <Link to="/login" className="text-[#32CD32] hover:underline" data-testid="reset-password-login-link">
            Entrar agora
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight uppercase mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
          Nova palavra-passe
        </h1>
        <p className="text-gray-400">
          Define a nova palavra-passe para <span className="text-white">{email}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-gray-300">
            Nova palavra-passe
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Mínimo 8 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-12 pr-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
              data-testid="reset-password-toggle-visibility"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm" className="text-sm font-medium text-gray-300">
            Confirmar palavra-passe
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              placeholder="Repete a palavra-passe"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
              data-testid="reset-password-confirm-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full btn-primary h-14 rounded-xl text-lg"
          disabled={loading}
          data-testid="reset-password-submit-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'GUARDAR PALAVRA-PASSE'}
        </Button>
      </form>

      <div className="mt-8 text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao login
        </Link>
      </div>
    </Shell>
  );
};

export default ResetPasswordPage;
